// Public landing target for an invite link (/link/:code). Nothing happens here
// server-side — the code is redeemed inside the app, so the page just shows it
// back in readable form and points at the stores. English only, like the
// landing page (the legal pages are the localised ones).
import { LegalShell } from './_LegalPage';
import { StoreBadges } from '../components/StoreBadges';
import { usePageMeta } from '../lib/meta';

// Crockford base32 minus the ambiguous letters, matching the backend alphabet.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_LEN = 8;

/** Folds a URL segment to the canonical code, resolving the Crockford
 *  confusions (I/L→1, O→0) exactly as the server does. Returns null when it
 *  isn't a code, so a mistyped link shows the fallback copy instead of garbage. */
export function normalizeCode(raw: string | undefined): string | null {
  if (!raw) return null;
  let out = '';
  for (const ch of decodeURIComponent(raw).toUpperCase()) {
    if ('- _.'.includes(ch)) continue;
    const c = ch === 'I' || ch === 'L' ? '1' : ch === 'O' ? '0' : ch;
    if (!ALPHABET.includes(c)) return null;
    if (out.length === CODE_LEN) return null;
    out += c;
  }
  return out.length === CODE_LEN ? out : null;
}

export function LinkInvite({ code }: { code?: string }) {
  usePageMeta('Join a household on shotmeal');
  const normalized = normalizeCode(code);
  const display = normalized
    ? `${normalized.slice(0, 4)}-${normalized.slice(4)}`
    : null;

  return (
    <LegalShell title="Household invite">
      <p>
        You've been invited to link households on <strong>shotmeal</strong>.
        Linked accounts see each other's diary, and can log meals for each other.
      </p>

      {display ? (
        <>
          <p>
            Open the app → <strong>Settings</strong> → <strong>Household</strong> →{' '}
            <strong>Enter code</strong>:
          </p>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'clamp(28px, 6vw, 40px)',
              fontWeight: 700,
              letterSpacing: 2,
              color: 'var(--ink)',
              background: 'var(--surface-2)',
              borderRadius: 14,
              padding: '18px 20px',
              margin: '20px 0',
              textAlign: 'center',
              userSelect: 'all',
            }}
          >
            {display}
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>
            The code works once and expires 24 hours after it was created. Ask for
            a new one if it's no longer accepted.
          </p>
        </>
      ) : (
        <p>
          This invite link looks incomplete. Ask the person who sent it to share
          the code again from the app (<strong>Settings</strong> →{' '}
          <strong>Household</strong>).
        </p>
      )}

      <p style={{ marginTop: 32 }}>Don't have the app yet?</p>
      <div style={{ margin: '16px 0 8px' }}>
        <StoreBadges />
      </div>
    </LegalShell>
  );
}
